import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdOnboardingsOptions } from '@onefootprint/fetch-dashboard';
import { IcoArrowTopRight16, IcoUser24 } from '@onefootprint/icons';
import type { Document } from '@onefootprint/request-types/dashboard';
import { Divider, LinkButton, SegmentedControl } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ONBOARDINGS_ID } from '@/entity/components/content/components/onboardings-shared/constants';
import { useEntityContext } from '@/entity/hooks/use-entity-context';
import useEntityId from '@/entity/hooks/use-entity-id';
import useSeqnoIdentityData from '../../hooks/use-seqno-identity-data';
import CollapsibleSection from '../collapsible-section';
import IdentityDataContent from './components/identity-data-content';
export type IdentityDataProps = {
  document: Document;
};

// This component shows the identity data from the document's onboarding and the latest identity data for the vault
// In the case that the document's onboarding is the latest, only the latest identity data is shown (no SegmentedControl)
const IdentityData = t0 => {
  const $ = _c(56);
  const {
    document
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset.documents.details.identity"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const [dataShown, setDataShown] = useState("latest");
  const fpId = useEntityId();
  const {
    kind: entityKind
  } = useEntityContext();
  let t2;
  if ($[1] !== fpId) {
    t2 = getEntitiesByFpIdOnboardingsOptions({
      path: {
        fpId
      },
      query: {
        pageSize: 20
      }
    });
    $[1] = fpId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = {
      ...t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: onboardings
  } = useQuery(t3);
  let t4;
  if ($[5] !== document.onboardingId || $[6] !== onboardings?.data) {
    t4 = onboardings?.data?.find(ob => ob.id === document.onboardingId);
    $[5] = document.onboardingId;
    $[6] = onboardings?.data;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onboarding = t4;
  const seqno = dataShown === "latest" ? undefined : onboarding?.seqno;
  let t5;
  if ($[8] !== document.kind || $[9] !== fpId || $[10] !== seqno) {
    t5 = {
      fpId,
      seqno,
      documentKind: document.kind
    };
    $[8] = document.kind;
    $[9] = fpId;
    $[10] = seqno;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    vault,
    isDecrypted,
    canDecrypt,
    dis
  } = useSeqnoIdentityData(t5);
  let t6;
  if ($[12] !== document.onboardingId || $[13] !== entityKind || $[14] !== fpId) {
    t6 = () => {
      const page = entityKind === "person" ? "users" : "businesses";
      return `/${page}/${fpId}?onboarding_id=${document.onboardingId}#${ONBOARDINGS_ID}`;
    };
    $[12] = document.onboardingId;
    $[13] = entityKind;
    $[14] = fpId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const getOnboardingLink = t6;
  let t7;
  if ($[16] !== t) {
    t7 = t(_temp);
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t(_temp2);
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t(_temp3);
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t9) {
    t10 = {
      label: t9,
      value: "latest"
    };
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t(_temp4);
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = {
      label: t11,
      value: "onboarding"
    };
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t12) {
    t13 = [t10, t12];
    $[28] = t10;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== dataShown || $[32] !== t13 || $[33] !== t8) {
    t14 = <div className="**:w-full"><SegmentedControl size="compact" aria-label={t8} options={t13} value={dataShown} onChange={setDataShown} /></div>;
    $[31] = dataShown;
    $[32] = t13;
    $[33] = t8;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== canDecrypt || $[36] !== dis || $[37] !== document.kind || $[38] !== isDecrypted || $[39] !== seqno || $[40] !== vault) {
    t15 = <IdentityDataContent documentKind={document.kind} vault={vault} isDecrypted={isDecrypted} canDecrypt={canDecrypt} dis={dis} seqno={seqno} />;
    $[35] = canDecrypt;
    $[36] = dis;
    $[37] = document.kind;
    $[38] = isDecrypted;
    $[39] = seqno;
    $[40] = vault;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== document.onboardingId || $[43] !== getOnboardingLink || $[44] !== seqno || $[45] !== t) {
    t16 = !!seqno && document.onboardingId && <><Divider /><div className="flex justify-center"><LinkButton iconComponent={IcoArrowTopRight16} href={getOnboardingLink()} target="_blank">{t(_temp5)}</LinkButton></div></>;
    $[42] = document.onboardingId;
    $[43] = getOnboardingLink;
    $[44] = seqno;
    $[45] = t;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t15 || $[48] !== t16) {
    t17 = <div className="flex flex-col gap-3 w-full">{t15}{t16}</div>;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t14 || $[51] !== t17) {
    t18 = <div className="flex flex-col gap-4">{t14}{t17}</div>;
    $[50] = t14;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t18 || $[54] !== t7) {
    t19 = <CollapsibleSection icon={IcoUser24} title={t7} defaultOpen={true}>{t18}</CollapsibleSection>;
    $[53] = t18;
    $[54] = t7;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  return t19;
};
export default IdentityData;
function _temp($) {
  return $.title;
}
function _temp2($_0) {
  return $_0.dataShown.ariaLabel;
}
function _temp3($_1) {
  return $_1.dataShown.latest;
}
function _temp4($_2) {
  return $_2.dataShown.onboarding;
}
function _temp5($_3) {
  return $_3.viewOnboarding;
}